import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

/**
 * Tabs Component
 * Shows tabs on desktop (≥750px), and tabs or accordion on mobile (<750px)
 *
 * @author: Alec Hewstone (Kubix Media)
 * @version: 1.0.0
 * @date: 08/12/2025
 * @description: Custom tabs component with accordion fallback for mobile.
 */

class TabsComponent extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    
    this.#init();
    
    // Re-initialize on breakpoint change
    mediaQueryLarge.addEventListener('change', () => this.#init(), { signal });
    
    // Add keyboard navigation
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #init() {
    const isMobile = isMobileBreakpoint();
    const mobileMode = this.dataset.mobileMode || this.getAttribute('mobile-mode') || 'accordion';
    
    if (isMobile && mobileMode === 'accordion') {
      this.#showAsAccordion();
    } else {
      this.#showAsTabs();
    }
  }

  #showAsAccordion() {
    // Remove tabs navigation wrapper if it exists (this also removes arrows)
    const existingNavWrap = this.querySelector('.tabs-navigation-wrap');
    if (existingNavWrap) existingNavWrap.remove();
    
    // Also remove standalone navigation if it exists
    const existingNav = this.querySelector('.tabs-navigation');
    if (existingNav && !existingNav.parentElement?.classList.contains('tabs-navigation-wrap')) {
      existingNav.remove();
    }
    
    // Enable accordion behavior
    this.querySelectorAll('accordion-custom').forEach(acc => {
      if (acc instanceof HTMLElement) {
        delete acc.dataset.disableOnDesktop;
        acc.classList.remove('is-active');
      }
    });
    
    this.classList.remove('tabs-mode');
    this.classList.add('accordion-mode');
  }

  #showAsTabs() {
    this.classList.add('tabs-mode');
    this.classList.remove('accordion-mode');
    
    // Disable accordion click behavior on desktop
    this.querySelectorAll('accordion-custom').forEach(acc => {
      if (acc instanceof HTMLElement) {
        acc.dataset.disableOnDesktop = 'true';
      }
    });
    
    // Get all accordion-custom elements (our panels)
    const panels = Array.from(this.querySelectorAll('accordion-custom'));
    
    if (panels.length === 0) return;
    
    // Hide tabs if only one panel
    if (panels.length === 1) {
      this.classList.add('single-tab');
      return;
    }
    
    this.classList.remove('single-tab');
    
    // Create tabs navigation wrapper (for fade gradients and arrows)
    let tabsNavWrap = this.querySelector('.tabs-navigation-wrap');
    let tabsNav = this.querySelector('.tabs-navigation');
    
    if (!tabsNavWrap) {
      tabsNavWrap = document.createElement('div');
      tabsNavWrap.className = 'tabs-navigation-wrap';
    }
    
    if (!tabsNav) {
      tabsNav = document.createElement('div');
      tabsNav.className = 'tabs-navigation';
      tabsNav.setAttribute('role', 'tablist');
      
      // Create tab button for each panel
      panels.forEach((panel, index) => {
        const summary = panel.querySelector('summary');
        if (!summary) return;
        
        // Extract heading text (remove icon spans)
        const headingText = Array.from(summary.childNodes)
          .filter(node => node.nodeType === Node.TEXT_NODE)
          .map(node => node.textContent?.trim() || '')
          .join(' ')
          .trim();
        
        // Skip if empty heading
        if (!headingText) {
          return;
        }
        
        const button = document.createElement('button');
        button.className = 'tabs-button';
        button.id = `tab-${index}`;
        button.textContent = headingText;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', `panel-${index}`);
        button.setAttribute('tabindex', index === 0 ? '0' : '-1');
        button.dataset.panelIndex = String(index);
        
        button.addEventListener('click', () => this.#switchTab(Number(index), true));
        
        if (tabsNav) {
          tabsNav.appendChild(button);
        }
        
        // Set IDs and ARIA on panels
        if (panel instanceof HTMLElement) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('id', `panel-${index}`);
          panel.setAttribute('aria-labelledby', `tab-${index}`);
        }
      });
      
      tabsNavWrap.appendChild(tabsNav);
      
      // Create ARIA live region for screen reader announcements
      const liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.id = 'tabs-status';
      tabsNavWrap.appendChild(liveRegion);
      
      // Add arrow buttons for mobile
      const mobileMode = this.getAttribute('mobile-mode') || 'accordion';
      if (isMobileBreakpoint() && mobileMode === 'tabs' && tabsNavWrap instanceof HTMLElement) {
        this.#addArrowButtons(tabsNavWrap);
      }
      
      // Insert navigation wrapper before panels
      if (this.firstChild) {
        this.insertBefore(tabsNavWrap, this.firstChild);
      } else {
        this.appendChild(tabsNavWrap);
      }
    } else if (!tabsNav.parentElement || tabsNav.parentElement.className !== 'tabs-navigation-wrap') {
      // Move existing nav into wrapper
      if (!tabsNavWrap.contains(tabsNav)) {
        tabsNavWrap.appendChild(tabsNav);
        if (this.firstChild) {
          this.insertBefore(tabsNavWrap, this.firstChild);
        } else {
          this.appendChild(tabsNavWrap);
        }
      }
      
      // Add arrows if on mobile and don't exist
      const mobileMode = this.getAttribute('mobile-mode') || 'accordion';
      if (isMobileBreakpoint() && mobileMode === 'tabs' && tabsNavWrap instanceof HTMLElement && !tabsNavWrap.querySelector('.tabs-nav-arrow')) {
        this.#addArrowButtons(tabsNavWrap);
      }
    }
    
    // Initialize arrow functionality
    const mobileMode = this.getAttribute('mobile-mode') || 'accordion';
    if (isMobileBreakpoint() && mobileMode === 'tabs') {
      this.#initArrowNavigation();
    }
    
    // Activate first tab and initialize underline (wait for DOM to be ready)
    requestAnimationFrame(() => {
      this.#switchTab(0, false);
    });
  }
  
  /**
   * Add prev/next arrow buttons for mobile navigation
   * @param {HTMLElement} navWrap - The navigation wrapper element
   */
  #addArrowButtons(navWrap) {
    // Check if arrows already exist
    if (navWrap.querySelector('.tabs-nav-arrow')) return;
    
    // Create prev arrow
    const prevArrow = document.createElement('button');
    prevArrow.className = 'tabs-nav-arrow tabs-nav-arrow--prev';
    prevArrow.setAttribute('aria-label', 'Previous tab');
    prevArrow.setAttribute('type', 'button');
    prevArrow.hidden = true;
    prevArrow.innerHTML = '<span class="svg-wrapper icon-arrow"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.5 3L5 7L8.5 11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
    
    // Create next arrow
    const nextArrow = document.createElement('button');
    nextArrow.className = 'tabs-nav-arrow tabs-nav-arrow--next';
    nextArrow.setAttribute('aria-label', 'Next tab');
    nextArrow.setAttribute('type', 'button');
    nextArrow.hidden = true;
    nextArrow.innerHTML = '<span class="svg-wrapper icon-arrow"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.5 3L9 7L5.5 11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
    
    navWrap.appendChild(prevArrow);
    navWrap.appendChild(nextArrow);
  }
  
  /**
   * Initialize arrow navigation functionality
   */
  #initArrowNavigation() {
    const navWrap = this.querySelector('.tabs-navigation-wrap');
    const nav = this.querySelector('.tabs-navigation');
    const prevBtn = this.querySelector('.tabs-nav-arrow--prev');
    const nextBtn = this.querySelector('.tabs-nav-arrow--next');
    
    if (!nav || !navWrap) return;
    
    // Bind arrow click handlers
    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.#navigateTabs('prev'), { signal: this.#controller.signal });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.#navigateTabs('next'), { signal: this.#controller.signal });
    }
    
    // Update arrows on scroll
    nav.addEventListener('scroll', () => this.#updateArrows(), { passive: true, signal: this.#controller.signal });
    
    // Update arrows on resize
    window.addEventListener('resize', () => this.#updateArrows(), { passive: true, signal: this.#controller.signal });
    
    // Initial update
    requestAnimationFrame(() => this.#updateArrows());
  }
  
  /**
   * Navigate tabs using arrow buttons
   * @param {string} direction - 'prev' or 'next'
   */
  #navigateTabs(direction) {
    const buttons = Array.from(this.querySelectorAll('.tabs-button'));
    const activeButton = buttons.find(btn => btn.classList.contains('is-active'));
    
    if (!activeButton || buttons.length === 0) return;
    
    const currentIndex = buttons.indexOf(activeButton);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
    }
    
    // Switch to new tab
    this.#switchTab(newIndex, true);
    
    // Scroll to show active tab
    this.#scrollToActiveTab(newIndex);
  }
  
  /**
   * Scroll navigation to show active tab button
   * @param {number} index - Tab index
   */
  #scrollToActiveTab(index) {
    const nav = this.querySelector('.tabs-navigation');
    const buttons = Array.from(this.querySelectorAll('.tabs-button'));
    const activeButton = buttons[index];
    
    if (!nav || !activeButton) return;
    
    const buttonRect = activeButton.getBoundingClientRect();
    const navRect = nav.getBoundingClientRect();
    
    // Calculate scroll position to center the active tab
    const scrollLeft = nav.scrollLeft + (buttonRect.left - navRect.left) - (navRect.width / 2) + (buttonRect.width / 2);
    
    nav.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  }
  
  /**
   * Update arrow visibility and fade gradients based on scroll position
   */
  #updateArrows() {
    const navWrap = this.querySelector('.tabs-navigation-wrap');
    const nav = this.querySelector('.tabs-navigation');
    const prevBtn = this.querySelector('.tabs-nav-arrow--prev');
    const nextBtn = this.querySelector('.tabs-nav-arrow--next');
    
    if (!nav || !navWrap) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = nav;
    const isAtStart = scrollLeft <= 5;
    const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 5;
    const canScroll = scrollWidth > clientWidth;
    
    // Update fade gradients
    navWrap.classList.toggle('fade-left', !isAtStart && canScroll);
    navWrap.classList.toggle('fade-right', !isAtEnd && canScroll);
    
    // Update arrow visibility
    if (prevBtn instanceof HTMLElement) {
      prevBtn.hidden = isAtStart || !canScroll;
    }
    if (nextBtn instanceof HTMLElement) {
      nextBtn.hidden = isAtEnd || !canScroll;
    }
  }

  /**
   * Switch to a specific tab by index
   * @param {number} index - The index of the tab to switch to
   * @param {boolean} shouldFocus - Whether to focus the button (default: false)
   */
  #switchTab(index, shouldFocus = false) {
    const panels = Array.from(this.querySelectorAll('accordion-custom'));
    const buttons = Array.from(this.querySelectorAll('.tabs-button'));
    
    if (index < 0 || index >= panels.length) return;
    
    // Update panels
    panels.forEach((panel, i) => {
      const details = panel.querySelector('details');
      if (i === index) {
        panel.classList.add('is-active');
        if (details instanceof HTMLDetailsElement) {
          details.open = true;
        }
      } else {
        panel.classList.remove('is-active');
        if (details instanceof HTMLDetailsElement) {
          details.open = false;
        }
      }
    });
    
    // Update buttons and calculate sliding underline position
    const tabsNav = /** @type {HTMLElement | null} */ (this.querySelector('.tabs-navigation'));
    let activeButton = /** @type {HTMLElement | null} */ (null);
    
    buttons.forEach((button, i) => {
      if (i === index) {
        button.classList.add('is-active');
        button.setAttribute('aria-selected', 'true');
        button.setAttribute('tabindex', '0');
        activeButton = /** @type {HTMLElement} */ (button);
        // Only focus if explicitly requested (user interaction)
        if (shouldFocus && button instanceof HTMLElement) {
          button.focus();
        }
      } else {
        button.classList.remove('is-active');
        button.setAttribute('aria-selected', 'false');
        button.setAttribute('tabindex', '-1');
      }
    });
    
    // Announce tab change to screen readers
    const liveRegion = this.querySelector('#tabs-status');
    if (liveRegion && activeButton) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const liveElement = /** @type {any} */ (liveRegion);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const buttonElement = /** @type {any} */ (activeButton);
      if (liveElement.textContent !== undefined && buttonElement.textContent !== undefined) {
        liveElement.textContent = `${buttonElement.textContent} tab selected`;
      }
    }
    
    // Update sliding underline position
    if (tabsNav && activeButton) {
      // TypeScript: querySelector returns Element | null, but we know these are HTMLElements
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const navElement = /** @type {any} */ (tabsNav);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const buttonElement = /** @type {any} */ (activeButton);
      
      if (navElement.getBoundingClientRect && buttonElement.getBoundingClientRect) {
        // Use requestAnimationFrame to ensure smooth transition
        requestAnimationFrame(() => {
          const navRect = navElement.getBoundingClientRect();
          const buttonRect = buttonElement.getBoundingClientRect();
          const left = buttonRect.left - navRect.left;
          const width = buttonRect.width;
          
          // Update CSS custom properties to trigger sliding animation
          navElement.style.setProperty('--underline-left', `${left}px`);
          navElement.style.setProperty('--underline-width', `${width}px`);
        });
      }
    }
    
    // Update arrows and scroll on mobile
    const mobileMode = this.getAttribute('mobile-mode') || 'accordion';
    if (isMobileBreakpoint() && mobileMode === 'tabs') {
      requestAnimationFrame(() => {
        this.#updateArrows();
        this.#scrollToActiveTab(index);
      });
    }
  }

  /**
   * Handle keyboard navigation for tabs
   * @param {KeyboardEvent} event - The keyboard event
   */
  #handleKeyDown = (event) => {
    // Only handle keyboard navigation in tabs mode
    if (!this.classList.contains('tabs-mode') || this.classList.contains('single-tab')) {
      return;
    }
    
    const buttons = Array.from(this.querySelectorAll('.tabs-button'));
    const activeButton = buttons.find(btn => btn.getAttribute('aria-selected') === 'true');
    
    if (!activeButton) return;
    
    const currentIndex = buttons.indexOf(activeButton);
    if (currentIndex === -1) return;
    
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
        this.#switchTab(prevIndex, true);
        break;
        
      case 'ArrowRight':
        event.preventDefault();
        const nextIndex = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
        this.#switchTab(nextIndex, true);
        break;
        
      case 'Home':
        event.preventDefault();
        this.#switchTab(0, true);
        break;
        
      case 'End':
        event.preventDefault();
        this.#switchTab(buttons.length - 1, true);
        break;
    }
  };
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
